import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Check, CreditCard, Zap, Building2, Users, Package, AlertTriangle, Loader2, CheckCircle2, Landmark, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [bankTransferOpen, setBankTransferOpen] = useState(false);
  const [bankTransferPlan, setBankTransferPlan] = useState(null);
  const [bankTransferEmail, setBankTransferEmail] = useState('');
  const [sendingBankEmail, setSendingBankEmail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/current')
      ]);
      setPlans(plansRes.data);
      setCurrentSubscription(subRes.data);
    } catch (error) {
      toast.error('Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check payment status on return from Stripe
  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setCheckingPayment(false);
        toast.error('Payment verification timed out. Please check your email for confirmation.');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setCheckingPayment(false);
          toast.success('Payment successful! Your subscription is now active.');
          // Clear URL params
          setSearchParams({});
          // Refresh subscription data
          fetchData();
          return;
        } else if (data.status === 'expired') {
          setCheckingPayment(false);
          toast.error('Payment session expired. Please try again.');
          setSearchParams({});
          return;
        }

        // Continue polling
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setCheckingPayment(false);
        toast.error('Error checking payment status');
      }
    };

    await poll();
  }, [fetchData, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams, checkPaymentStatus, setSearchParams]);

  const handleUpgrade = async (planId) => {
    setProcessingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payments/create-checkout', {
        plan_id: planId,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPlan(null);
    }
  };

  const handleBankTransfer = (planId) => {
    setBankTransferPlan(planId);
    setBankTransferEmail('');
    setBankTransferOpen(true);
  };

  const getBankTransferAmount = () => {
    if (!bankTransferPlan) return 'J$0';
    const plan = plans.find(p => p.id === bankTransferPlan);
    if (plan?.price_monthly) return `J$${plan.price_monthly.toLocaleString()}`;
    const amounts = { 'plan_basic': 'J$2,499', 'plan_growth': 'J$4,999', 'plan_pro': 'J$9,999' };
    return amounts[bankTransferPlan] || 'J$0';
  };

  const sendBankTransferEmail = async () => {
    if (!bankTransferEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setSendingBankEmail(true);
    try {
      const res = await api.post('/subscription/bank-transfer', {
        plan_id: bankTransferPlan,
        email: bankTransferEmail
      });
      toast.success(res.data.message);
      setBankTransferOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bank details');
    } finally {
      setSendingBankEmail(false);
    }
  };

  const getUsagePercent = (used, max) => {
    if (max === -1) return 0;
    return Math.min((used / max) * 100, 100);
  };

  const formatLimit = (value) => {
    if (value === undefined || value === null) return '0';
    if (value === -1) return 'Unlimited';
    return value.toLocaleString();
  };

  const formatPrice = (price, planId) => {
    const jmdPrices = {
      'plan_basic': 'J$2,499',
      'plan_growth': 'J$4,999',
      'plan_pro': 'J$9,999',
    };
    if (jmdPrices[planId]) return jmdPrices[planId];
    if (price && price > 0) return `J$${price.toLocaleString()}`;
    return 'J$0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  const currentPlan = currentSubscription?.plan;
  const usage = currentSubscription?.usage;
  const subscription = currentSubscription?.subscription;
  const isTrialing = subscription?.status === 'trial';
  const isPaid = subscription?.payment_verified === true;
  const trialEndsAt = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const daysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">BILLING</h1>
        <p className="text-[#0A0A0A]/60 mt-1">Manage your subscription and billing</p>
      </div>

      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <Card className="border-2 border-[#007AFF] shadow-none bg-[#007AFF]/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" />
              <div>
                <p className="text-heading text-lg text-[#0A0A0A]">Verifying Payment...</p>
                <p className="text-[#0A0A0A]/60">Please wait while we confirm your payment.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial Banner */}
      {isTrialing && !checkingPayment && (
        <Card className="border-2 border-[#FF9500] shadow-none bg-[#FF9500]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FF9500]/10 rounded-sm flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#FF9500]" />
                </div>
                <div>
                  <p className="text-heading text-lg text-[#0A0A0A]">Free Trial</p>
                  <p className="text-[#0A0A0A]/60">
                    {daysLeft > 0 
                      ? `${daysLeft} days remaining in your trial`
                      : 'Your trial has ended'}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-[#FF9500] hover:bg-[#FF9500]/90 text-white" 
                data-testid="upgrade-now-btn"
                onClick={() => document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid Status Banner */}
      {isPaid && !isTrialing && (
        <Card className="border-2 border-[#34C759] shadow-none bg-[#34C759]/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#34C759]/10 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#34C759]" />
              </div>
              <div>
                <p className="text-heading text-lg text-[#0A0A0A]">Subscription Active</p>
                <p className="text-[#0A0A0A]/60">Your payment has been verified. Thank you for subscribing!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan & Usage */}
      {currentPlan && usage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Plan */}
          <Card className="border border-[#E5E5EA] shadow-none">
            <CardHeader className="border-b border-[#E5E5EA] pb-4">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#007AFF]" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-heading text-2xl text-[#0A0A0A]">{currentPlan.name}</p>
                  <p className="text-[#0A0A0A]/60">{currentPlan.description}</p>
                </div>
                <Badge className={isTrialing ? 'bg-[#FF9500]/10 text-[#FF9500]' : 'bg-[#34C759]/10 text-[#34C759]'}>
                  {isTrialing ? 'Trial' : 'Active'}
                </Badge>
              </div>
              <div className="text-3xl font-mono font-bold text-[#0A0A0A]">
                {formatPrice(currentPlan.price, currentPlan.id)}
                <span className="text-base font-normal text-[#0A0A0A]/60">/month</span>
              </div>
            </CardContent>
          </Card>

          {/* Usage */}
          <Card className="border border-[#E5E5EA] shadow-none">
            <CardHeader className="border-b border-[#E5E5EA] pb-4">
              <CardTitle className="text-heading text-lg">Usage</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Warehouses */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#0A0A0A]/60" />
                    <span className="text-sm font-medium">Warehouses</span>
                  </div>
                  <span className="text-sm font-mono">
                    {usage.warehouses} / {formatLimit(currentPlan.features?.warehouses)}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercent(usage.warehouses, currentPlan.features?.warehouses)} 
                  className="h-2"
                />
              </div>
              {/* Users */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#0A0A0A]/60" />
                    <span className="text-sm font-medium">Users</span>
                  </div>
                  <span className="text-sm font-mono">
                    {usage.users} / {formatLimit(currentPlan.features?.users)}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercent(usage.users, currentPlan.features?.users)} 
                  className="h-2"
                />
              </div>
              {/* SKUs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#0A0A0A]/60" />
                    <span className="text-sm font-medium">SKUs</span>
                  </div>
                  <span className="text-sm font-mono">
                    {usage.skus} / {formatLimit(currentPlan.features?.skus)}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercent(usage.skus, currentPlan.features?.skus)} 
                  className="h-2"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Available Plans */}
      <div id="plans-section">
        <h2 className="text-heading text-xl text-[#0A0A0A] mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrent = currentPlan?.id === plan.id;
            const isProcessing = processingPlan === plan.id;
            return (
              <Card 
                key={plan.id}
                className={`border shadow-none ${isCurrent ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-[#E5E5EA]'}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-heading text-xl text-[#0A0A0A]">{plan.name}</h3>
                    {isCurrent && (
                      <Badge className="bg-[#007AFF]/10 text-[#007AFF]">Current</Badge>
                    )}
                  </div>
                  <p className="text-[#0A0A0A]/60 text-sm mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-3xl font-mono font-bold text-[#0A0A0A]">{formatPrice(plan.price, plan.id)}</span>
                    <span className="text-[#0A0A0A]/60">/month</span>
                  </div>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-[#007AFF]" />
                      {formatLimit(plan.features?.warehouses)} Warehouses
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-[#007AFF]" />
                      {formatLimit(plan.features?.users)} Users
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Package className="w-4 h-4 text-[#007AFF]" />
                      {formatLimit(plan.features?.skus)} SKUs
                    </li>
                  </ul>

                  {isCurrent && !isTrialing ? (
                    <Button variant="outline" className="w-full" disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button 
                        className="w-full bg-[#007AFF] hover:bg-[#0066D6]"
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={isProcessing || checkingPayment}
                        data-testid={`select-plan-${plan.name.toLowerCase()}`}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay with Card
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full border-[#34C759] text-[#34C759] hover:bg-[#34C759]/5"
                        onClick={() => handleBankTransfer(plan.id)}
                        disabled={checkingPayment}
                        data-testid={`bank-transfer-${plan.name.toLowerCase()}`}
                      >
                        <Landmark className="w-4 h-4 mr-2" />
                        Bank Transfer
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment Security Notice */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#F5F5F7] rounded-sm flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[#0A0A0A]/60" />
            </div>
            <div>
              <p className="font-medium text-[#0A0A0A]">Secure Payments</p>
              <p className="text-sm text-[#0A0A0A]/60">
                Pay with card (powered by Stripe) or via bank transfer to Sagicor Bank.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Transfer Dialog */}
      <Dialog open={bankTransferOpen} onOpenChange={setBankTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-[#34C759]" />
              Bank Transfer Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F5F5F7] rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#0A0A0A]/60">Bank:</span><span className="font-semibold">Sagicor Bank</span></div>
              <div className="flex justify-between"><span className="text-[#0A0A0A]/60">Branch:</span><span className="font-semibold">Portmore</span></div>
              <div className="flex justify-between"><span className="text-[#0A0A0A]/60">Account Name:</span><span className="font-semibold">Tazney Gadgets and More</span></div>
              <div className="flex justify-between"><span className="text-[#0A0A0A]/60">Account #:</span><span className="font-mono font-semibold">5503462647</span></div>
              <div className="flex justify-between"><span className="text-[#0A0A0A]/60">Account Type:</span><span className="font-semibold">Business Savings</span></div>
            </div>
            <div className="bg-[#FFF3CD] border border-[#FF9500]/20 rounded-lg p-3 text-sm">
              <p className="font-semibold">Amount Due: {getBankTransferAmount()}/month</p>
              <p className="text-[#0A0A0A]/60 text-xs mt-1">Include your organization name as payment reference</p>
            </div>
            <div className="space-y-2">
              <Label>Send details to your email</Label>
              <Input
                type="email"
                value={bankTransferEmail}
                onChange={(e) => setBankTransferEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="bank-transfer-email"
              />
              <p className="text-xs text-[#0A0A0A]/40">We'll send you the bank details and instructions via email</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBankTransferOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#34C759] hover:bg-[#2DB14E]"
              onClick={sendBankTransferEmail}
              disabled={sendingBankEmail}
              data-testid="send-bank-details-btn"
            >
              {sendingBankEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {sendingBankEmail ? 'Sending...' : 'Send Bank Details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
